/**
 * Shared offer helpers for the recruiter's Offer Management page and the
 * manager's Offers pages.
 *
 * Offer lifecycle (backend offerController): the recruiter submits a Draft for
 * approval, a manager approves it (Out_for_Signature) or rejects it back to
 * Draft with a reason, then the recruiter sends it and the candidate accepts
 * or declines.
 */
import { format } from "date-fns";

export const OFFER_STATUS = Object.freeze({
  DRAFT: "Draft",
  PENDING_APPROVAL: "Pending_Approval",
  OUT_FOR_SIGNATURE: "Out_for_Signature",
  ACCEPTED: "Accepted",
  DECLINED: "Declined",
});

// offer_text of a draft created before its letter was generated.
const OFFER_TEXT_PLACEHOLDER = "Generated HTML from template goes here";

// A template placeholder left in a generated letter, e.g. "{{compensation}}".
const UNFILLED_PLACEHOLDER = /\{\{\s*[\w.]+\s*\}\}/;

// candidate_message_id values set when a candidate accepts (backend acceptOffer).
const DOCS_PENDING = "SENT_VIA_EMAIL";
const NYLAS_DOCS_PREFIX = "nylas://";

const MS_PER_HOUR = 1000 * 60 * 60;

/** Whether the offer has a generated offer letter. */
export const hasOfferLetter = (offer) =>
  Boolean(offer?.offer_text && offer.offer_text.trim() !== "" && offer.offer_text !== OFFER_TEXT_PLACEHOLDER);

/** Whether the recruiter may still edit the letter (drafts only — also after a rejection). */
export const isEditableOffer = (offer) => offer?.status === OFFER_STATUS.DRAFT;

/** Whether a generated letter still has template placeholders that were not filled. */
export const hasUnfilledPlaceholders = (offer) => hasOfferLetter(offer) && UNFILLED_PLACEHOLDER.test(offer.offer_text);

/** "1.5 days", "3.2 hrs", "12 mins" — or "-" without a value. */
export const formatDuration = (hours) => {
  if (hours === null || hours === undefined) return "-";
  if (hours < 1) {
    const mins = Math.max(1, Math.round(hours * 60));
    return `${mins} min${mins > 1 ? "s" : ""}`;
  }
  if (hours < 24) return `${hours.toFixed(1)} hrs`;
  const days = (hours / 24).toFixed(1);
  return `${days} day${Number(days) > 1 ? "s" : ""}`;
};

/**
 * Offer KPIs: offers sent (email delivered), accepted, declined, acceptance
 * rate (accepted / sent, %) and the average time from sending to accepting.
 */
export const getOfferStats = (offers) => {
  const list = Array.isArray(offers) ? offers : [];
  const sent = list.filter((o) => Boolean(o.sent_date)).length;
  const acceptedOffers = list.filter((o) => o.status === OFFER_STATUS.ACCEPTED);
  const declined = list.filter((o) => o.status === OFFER_STATUS.DECLINED).length;

  const acceptHours = acceptedOffers
    .filter((o) => o.sent_date && o.signed_date)
    .map((o) => (new Date(o.signed_date) - new Date(o.sent_date)) / MS_PER_HOUR)
    .filter((hours) => hours > 0);
  const avgHours = acceptHours.length ? acceptHours.reduce((sum, h) => sum + h, 0) / acceptHours.length : null;

  return {
    sent,
    accepted: acceptedOffers.length,
    declined,
    acceptanceRate: sent === 0 ? 0 : Math.round((acceptedOffers.length / sent) * 100),
    avgTimeToAcceptHours: avgHours,
    avgTimeToAccept: formatDuration(avgHours),
    acceptedWithTimes: acceptHours.length,
  };
};

const formatDate = (value) => (value ? format(new Date(value), "dd MMM yyyy") : null);

/** One-line status for a list row, e.g. "Accepted 03 Oct 2026". */
export const offerStatusLabel = (offer) => {
  switch (offer?.status) {
    case OFFER_STATUS.DRAFT:
      return offer.rejection_reason ? "Rejected - back with recruiter" : "Draft - with recruiter";
    case OFFER_STATUS.PENDING_APPROVAL:
      return "Waiting for approval";
    case OFFER_STATUS.OUT_FOR_SIGNATURE:
      return offer.sent_date ? `Sent ${formatDate(offer.sent_date)}` : "Approved - ready to send";
    case OFFER_STATUS.ACCEPTED:
      return `Accepted ${formatDate(offer.signed_date) || ""}`.trim();
    case OFFER_STATUS.DECLINED:
      return "Declined";
    default:
      return offer?.status ? String(offer.status).replace(/_/g, " ") : "—";
  }
};

/** Sentence for the top of an offer's detail page. */
export const offerStatusSummary = (offer) => {
  const candidate = offer?.profile?.candidate_name || "The candidate";
  const stage = offer?.profile?.detailed_status ? String(offer.profile.detailed_status).replace(/_/g, " ") : null;
  switch (offer?.status) {
    case OFFER_STATUS.DRAFT:
      return offer.rejection_reason
        ? `Rejected${offer.reviewed_by_name ? ` by ${offer.reviewed_by_name}` : ""}: ${offer.rejection_reason}. Back with the recruiter.`
        : "Draft with the recruiter. It comes to Approvals when submitted.";
    case OFFER_STATUS.PENDING_APPROVAL:
      return `Waiting for manager approval${offer.submitted_by_name ? ` - submitted by ${offer.submitted_by_name}` : ""}.`;
    case OFFER_STATUS.OUT_FOR_SIGNATURE:
      return offer.sent_date
        ? `Sent on ${formatDate(offer.sent_date)}, waiting for ${candidate}'s answer.`
        : `Approved${offer.reviewed_by_name ? ` by ${offer.reviewed_by_name}` : ""}, ready for the recruiter to send.`;
    case OFFER_STATUS.ACCEPTED:
      return `Accepted on ${formatDate(offer.signed_date) || "—"}.${stage ? ` ${candidate} is at ${stage}.` : ""}`;
    case OFFER_STATUS.DECLINED:
      return `${candidate} declined the offer.`;
    default:
      return "";
  }
};

/** Candidate documents after acceptance: "none" (not accepted), "pending", "download" (Nylas) or "link" (URL). */
export const offerDocsState = (offer) => {
  if (offer?.status !== OFFER_STATUS.ACCEPTED) return "none";
  const ref = offer.candidate_message_id;
  if (!ref || ref === DOCS_PENDING) return "pending";
  if (ref.startsWith(NYLAS_DOCS_PREFIX)) return "download";
  return /^https?:\/\//i.test(ref) ? "link" : "pending";
};

/** The job's budget as shown on the job, e.g. "INR 1200000 Annual". */
export const jobBudgetLabel = (job) =>
  [job?.currency, job?.budget, job?.budget_uom].filter((part) => part !== null && part !== undefined && part !== "").join(" ") || null;
